/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

import { API_BASE } from "../config/apiConfig";

// ─── App Color Tokens (matching the platform exactly) ────────────────────────
const C = {
  // Backgrounds
  pageBg: "#f8fafc",
  cardBg: "#ffffff",
  cardHover: "#f8fafc",
  inputBg: "#f8fafc",
  // Text
  textMain: "#0f172a",
  textMuted: "#64748b",
  textLight: "#94a3b8",
  // Borders
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  // Primary (violet)
  primary: "#FD6101",
  primaryHov: "#e05300",
  primaryBg: "#fff7ed",
  primaryMid: "#ede9fe",
  // Accents
  accent: "#4f46e5",
  // Severity colors (matching SecurityConsole)
  critical: "#dc2626",
  criticalBg: "#fef2f2",
  criticalBdr: "#fca5a5",
  high: "#ea580c",
  highBg: "#fff7ed",
  highBdr: "#fdba74",
  medium: "#ca8a04",
  mediumBg: "#fefce8",
  mediumBdr: "#fde047",
  low: "#16a34a",
  lowBg: "#f0fdf4",
  lowBdr: "#86efac",
  passed: "#10b981",
  passedBg: "#ecfdf5",
  passedBdr: "#a7f3d0",
  // Shadow
  shadow: "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)",
  shadowLg: "0 10px 25px rgba(0,0,0,0.06)",
  shadowCard: "0 8px 24px rgba(0,0,0,0.03)",
};

const SEV = {
  critical: { color: C.critical, bg: C.criticalBg, border: C.criticalBdr, label: "Critical" },
  high: { color: C.high, bg: C.highBg, border: C.highBdr, label: "High" },
  medium: { color: C.medium, bg: C.mediumBg, border: C.mediumBdr, label: "Medium" },
  low: { color: C.low, bg: C.lowBg, border: C.lowBdr, label: "Low" },
  passed: { color: C.passed, bg: C.passedBg, border: C.passedBdr, label: "Passed" },
};

const scoreColor = (s) =>
  s >= 90 ? C.passed : s >= 70 ? C.low : s >= 50 ? C.medium : s >= 30 ? C.high : C.critical;

const fmt = (n) => (n || 0).toLocaleString();

// ─── Sub-components ──────────────────────────────────────────────────────────

const ScoreRing = ({ score = 0, size = 120, stroke = 10 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (score / 100);
  const color = scoreColor(score);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.borderLight} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dasharray 1s ease" }} />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em"
        style={{ fill: color, fontSize: size * 0.22, fontWeight: 800, fontFamily: "inherit" }}>
        {score}
      </text>
    </svg>
  );
};

const SevBadge = ({ sev }) => {
  const s = SEV[sev] || SEV.low;
  const icons = { critical: "🔴", high: "🟠", medium: "🟡", low: "🟢", passed: "✅" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {icons[sev] || "⚪"} {s.label}
    </span>
  );
};

const MetricCard = ({ icon, label, value, color, colorBg, sub }) => (
  <div style={{
    background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 18,
    padding: 24, position: "relative", overflow: "hidden",
    boxShadow: C.shadowCard, transition: "box-shadow 0.2s, transform 0.2s",
  }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = C.shadowLg; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = C.shadowCard; e.currentTarget.style.transform = "none"; }}>
    <div style={{ position: "absolute", top: -15, right: -15, width: 80, height: 80, borderRadius: "50%", background: `${color}0f` }} />
    <div style={{ fontSize: 12, color, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 38, fontWeight: 900, color, letterSpacing: -1 }}>{fmt(value)}</div>
    {sub && (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${color}18`, color, padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, marginTop: 8 }}>
        {icon} {sub}
      </div>
    )}
  </div>
);

const ProgressBar = ({ value = 0, color = C.primary, height = 8, label }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {label && (
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
        <span style={{ color: C.textMuted, fontWeight: 500 }}>{label}</span>
        <span style={{ color: C.textMain, fontWeight: 700 }}>{value}%</span>
      </div>
    )}
    <div style={{ background: C.borderLight, borderRadius: 20, height, overflow: "hidden" }}>
      <div style={{
        width: `${value}%`, height: "100%", borderRadius: 20,
        background: color, transition: "width 0.8s ease",
      }} />
    </div>
  </div>
);

// Pure SVG smooth cubic-bezier trend line
const TrendLine = ({ data = [], width = "100%", height = 130 }) => {
  if (!data || data.length < 2) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: C.textLight, fontSize: 13 }}>No trend data available</span>
      </div>
    );
  }

  const values = data.map(d => d.score);
  const minV = Math.min(...values, 40);
  const maxV = Math.max(...values, 100);
  const range = maxV - minV || 1;

  const widthPx = 500;
  const heightPx = 110;
  const padX = 20;
  const padY = 16;

  const points = data.map((d, i) => {
    const x = padX + (i / (data.length - 1)) * (widthPx - 2 * padX);
    const y = padY + (1 - (d.score - minV) / range) * (heightPx - 2 * padY);
    return { x, y, score: d.score, date: d.date };
  });

  let pathD = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const cpX1 = p1.x + (p2.x - p1.x) / 2;
    const cpY1 = p1.y;
    const cpX2 = p1.x + (p2.x - p1.x) / 2;
    const cpY2 = p2.y;
    pathD += ` C ${cpX1},${cpY1} ${cpX2},${cpY2} ${p2.x},${p2.y}`;
  }

  const fillD = `${pathD} L ${points[points.length - 1].x},${heightPx} L ${points[0].x},${heightPx} Z`;

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <svg viewBox={`0 0 ${widthPx} ${heightPx}`} preserveAspectRatio="none" style={{ width, height, overflow: "visible" }}>
        <defs>
          <linearGradient id="a11yTrendGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FD6101" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#FD6101" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Gradient fill */}
        <path d={fillD} fill="url(#a11yTrendGradient)" />

        {/* Bezier curve line */}
        <path d={pathD} fill="none" stroke="#FD6101" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

        {/* Graph dots */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="5" fill="#ffffff" stroke="#FD6101" strokeWidth="2" />
            <circle cx={p.x} cy={p.y} r="2.5" fill="#FD6101" />
          </g>
        ))}
      </svg>
    </div>
  );
};

// Rounded Bar chart
const BarChart = ({ data = [], height = 180 }) => {
  if (!data.length) return null;
  const maxVal = Math.max(...data.map(d => d.value), 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height, paddingBottom: 20, paddingTop: 10 }}>
      {data.map((d, i) => {
        const pct = Math.max(10, (d.value / maxVal) * 100);
        return (
          <div key={i} title={`${d.label}: ${d.value}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, height: "100%", justifyContent: "flex-end" }}>
            <span style={{ fontSize: 11, color: C.textMain, fontWeight: 800, marginBottom: 6 }}>{d.value}</span>
            <div style={{
              width: "100%", height: `${pct}%`,
              background: d.color || "#FD6101",
              borderRadius: "8px 8px 3px 3px",
              boxShadow: "0 4px 10px rgba(253, 97, 1, 0.15)",
              transition: "height 0.6s ease"
            }} />
            <span style={{ fontSize: 10, color: C.textMuted, fontWeight: 600, marginTop: 8, textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 60, textAlign: "center" }}>
              {d.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Code block
const CodeBlock = ({ code = "", language = "html" }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <div style={{ background: "#1e1e2e", borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 14px", background: "#16162a", borderBottom: "1px solid #2a2a4a" }}>
        <span style={{ fontSize: 11, color: "#7c6af7", fontWeight: 700, textTransform: "uppercase" }}>{language}</span>
        <button onClick={copy} style={{
          background: copied ? C.passed : "#2a2a4a", color: copied ? "#fff" : "#94a3b8",
          border: "none", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600,
        }}>{copied ? "✓ Copied" : "Copy"}</button>
      </div>
      <pre style={{ margin: 0, padding: "14px 16px", overflowX: "auto", fontSize: 12, color: "#cdd6f4", fontFamily: "'Fira Code', monospace", lineHeight: 1.6, maxHeight: 240, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {code}
      </pre>
    </div>
  );
};

// Tab bar (light-themed)
const TabBar = ({ tabs, active, onChange }) => (
  <div style={{ display: "flex", gap: 2, background: C.borderLight, borderRadius: 12, padding: 4, border: `1px solid ${C.border}`, flexWrap: "wrap" }}>
    {tabs.map(t => (
      <button key={t.id} onClick={() => onChange(t.id)} style={{
        padding: "8px 16px", border: "none", borderRadius: 8, cursor: "pointer",
        background: active === t.id ? C.cardBg : "transparent",
        color: active === t.id ? C.primary : C.textMuted,
        fontWeight: active === t.id ? 700 : 500, fontSize: 13,
        transition: "all 0.2s", display: "flex", alignItems: "center", gap: 6,
        boxShadow: active === t.id ? C.shadow : "none",
        whiteSpace: "nowrap",
      }}>
        <span>{t.icon}</span><span>{t.label}</span>
        {t.count != null && (
          <span style={{
            background: active === t.id ? C.primaryBg : C.borderLight,
            color: active === t.id ? C.primary : C.textLight,
            borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 700,
          }}>{t.count}</span>
        )}
      </button>
    ))}
  </div>
);

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_DASHBOARD = {
  overall_score: 67, critical_count: 12, high_count: 28, medium_count: 45, low_count: 31, passed_count: 58,
  total_scans: 7, wcag_a_pct: 84, wcag_aa_pct: 71, wcag_aaa_pct: 43,
  trend: [
    { date: "2026-07-22", score: 54 }, { date: "2026-07-23", score: 58 },
    { date: "2026-07-24", score: 61 }, { date: "2026-07-25", score: 63 },
    { date: "2026-07-26", score: 65 }, { date: "2026-07-27", score: 67 },
    { date: "2026-07-28", score: 67 },
  ],
  category_scores: { Images: 72, Forms: 58, Keyboard: 61, ARIA: 69, Contrast: 74, Navigation: 80, Tables: 91, Media: 55, Headings: 83 },
  top_violations: [
    { rule: "Image Missing Alt Text", category: "Images", severity: "critical", count: 24 },
    { rule: "Insufficient Color Contrast", category: "Color", severity: "high", count: 19 },
    { rule: "Form Input Missing Label", category: "Forms", severity: "high", count: 17 },
    { rule: "Button Missing Accessible Name", category: "Buttons", severity: "critical", count: 14 },
    { rule: "Missing Focus Indicator", category: "Focus", severity: "medium", count: 12 },
    { rule: "Page Missing H1", category: "Headings", severity: "medium", count: 9 },
    { rule: "Missing Skip Navigation Link", category: "Keyboard", severity: "medium", count: 8 },
    { rule: "Video Missing Captions", category: "Media", severity: "high", count: 7 },
  ],
};

const MOCK_ISSUES = [
  { id: 1, rule_name: "Image Missing Alt Text", category: "Images", severity: "critical", wcag_criterion: "1.1.1", wcag_level: "A", impact: "Completely blocks access for screen reader users", description: "Images must have an alt attribute.", why_failed: "This <img> element has no alt attribute. Screen readers will announce the image filename instead, which is meaningless to users.", affected_users: "Screen reader users, users with visual impairments", fix_suggestion: "Add a descriptive alt attribute to the img element.", source_snippet: '<img src="/assets/hero.jpg">', element_xpath: "//img[@src='/assets/hero.jpg']", element_css: "#hero-img", html_fix: '<img src="/assets/hero.jpg" alt="Hero banner showing our product lineup">', react_fix: '<img src={heroSrc} alt="Hero banner showing our product lineup" />', angular_fix: '<img [src]="heroSrc" alt="Hero banner showing our product lineup">', vue_fix: '<img :src="heroSrc" alt="Hero banner showing our product lineup">', priority: 1, est_fix_minutes: 120, page_url: "https://example.com" },
  { id: 2, rule_name: "Insufficient Color Contrast", category: "Color", severity: "high", wcag_criterion: "1.4.3", wcag_level: "AA", impact: "Severely impacts usability for low-vision users", description: "Text does not have sufficient contrast ratio (minimum 4.5:1).", why_failed: "The text color #aaa on white background only achieves a 2.32:1 contrast ratio, far below the 4.5:1 minimum.", affected_users: "Users with low vision, color blindness", fix_suggestion: "Increase contrast ratio to at least 4.5:1.", source_snippet: '<span style="color:#aaa">Subtitle text</span>', element_xpath: "//span[@class='subtitle']", element_css: ".subtitle", html_fix: '<span style="color:#595959">Subtitle text (4.54:1 ratio)</span>', react_fix: '<span style={{ color: "#595959" }}>Subtitle text</span>', angular_fix: '<span [ngStyle]="{color: \'#595959\'}">Subtitle text</span>', vue_fix: '<span :style="{color: \'#595959\'}">Subtitle text</span>', priority: 2, est_fix_minutes: 60, page_url: "https://example.com" },
  { id: 3, rule_name: "Form Input Missing Label", category: "Forms", severity: "high", wcag_criterion: "1.3.1", wcag_level: "A", impact: "Severely impacts form usability for screen reader users", description: "All form inputs must have an associated label element.", why_failed: "The email input field has no associated label. Screen readers cannot tell users what to type.", affected_users: "Screen reader users, users with cognitive disabilities", fix_suggestion: "Associate a label element with each input using the for/id pairing.", source_snippet: '<input type="email" placeholder="Enter email">', element_xpath: "//input[@type='email']", element_css: "#email", html_fix: '<label for="email">Email Address</label>\n<input type="email" id="email" name="email">', react_fix: '<label htmlFor="email">Email Address</label>\n<input type="email" id="email" />', angular_fix: '<label for="email">Email Address</label>\n<input type="email" id="email" formControlName="email">', vue_fix: '<label for="email">Email Address</label>\n<input type="email" id="email" v-model="email">', priority: 2, est_fix_minutes: 60, page_url: "https://example.com/contact" },
  { id: 4, rule_name: "Button Missing Accessible Name", category: "Buttons", severity: "critical", wcag_criterion: "4.1.2", wcag_level: "A", impact: "Completely blocks access — screen readers cannot describe this button", description: "Buttons must have an accessible name.", why_failed: "This icon-only button has no text content and no aria-label. Screen readers cannot determine what it does.", affected_users: "Screen reader users, keyboard-only users", fix_suggestion: "Add aria-label attribute to describe the button's action.", source_snippet: '<button class="btn-close">✕</button>', element_xpath: "//button[@class='btn-close']", element_css: ".btn-close", html_fix: '<button class="btn-close" aria-label="Close dialog">✕</button>', react_fix: '<button className="btn-close" aria-label="Close dialog">✕</button>', angular_fix: '<button class="btn-close" aria-label="Close dialog">✕</button>', vue_fix: '<button class="btn-close" aria-label="Close dialog">✕</button>', priority: 1, est_fix_minutes: 15, page_url: "https://example.com" },
  { id: 5, rule_name: "Missing Focus Indicator", category: "Focus", severity: "medium", wcag_criterion: "2.4.7", wcag_level: "AA", impact: "Creates friction for keyboard-only users", description: "Interactive elements do not have a visible focus indicator.", why_failed: "The CSS rule 'outline: none' has been applied globally, removing the default browser focus ring.", affected_users: "Keyboard-only users, users with motor impairments", fix_suggestion: "Ensure interactive elements have a visible :focus style.", source_snippet: "* { outline: none; }", element_xpath: "//a[@class='nav-link']", element_css: "a.nav-link:focus", html_fix: '<style>\n:focus {\n  outline: 3px solid #FD6101;\n  outline-offset: 2px;\n}\n</style>', react_fix: '/* global.css */\n:focus {\n  outline: 3px solid #FD6101;\n  outline-offset: 2px;\n}', angular_fix: '/* styles.css */\n:focus {\n  outline: 3px solid #FD6101;\n  outline-offset: 2px;\n}', vue_fix: '/* App.vue */\n:focus {\n  outline: 3px solid #FD6101;\n  outline-offset: 2px;\n}', priority: 3, est_fix_minutes: 30, page_url: "https://example.com" },
  { id: 6, rule_name: "Video Missing Captions", category: "Media", severity: "high", wcag_criterion: "1.2.2", wcag_level: "A", impact: "Deaf and hard-of-hearing users cannot access audio content", description: "Prerecorded video with audio must have synchronized captions.", why_failed: "This video element has no caption track.", affected_users: "Deaf and hard-of-hearing users", fix_suggestion: "Add a <track> element with kind='captions'.", source_snippet: '<video src="/intro.mp4" controls></video>', element_xpath: "//video[@src='/intro.mp4']", element_css: "#intro-video", html_fix: '<video controls>\n  <source src="/intro.mp4" type="video/mp4">\n  <track kind="captions" src="/captions.vtt" srclang="en" label="English" default>\n</video>', react_fix: '<video controls>\n  <source src={introSrc} type="video/mp4" />\n  <track kind="captions" src={captionsSrc} srcLang="en" label="English" default />\n</video>', angular_fix: '<video controls>\n  <source [src]="introSrc" type="video/mp4">\n  <track kind="captions" [src]="captionsSrc" srclang="en" label="English" default>\n</video>', vue_fix: '<video controls>\n  <source :src="introSrc" type="video/mp4">\n  <track kind="captions" :src="captionsSrc" srclang="en" label="English" default>\n</video>', priority: 2, est_fix_minutes: 60, page_url: "https://example.com/about" },
];

const MOCK_PAGES = [
  { id: 1, url: "https://example.com", title: "Home", score: 71, issue_count: 18 },
  { id: 2, url: "https://example.com/about", title: "About Us", score: 64, issue_count: 23 },
  { id: 3, url: "https://example.com/contact", title: "Contact", score: 55, issue_count: 31 },
  { id: 4, url: "https://example.com/products", title: "Products", score: 78, issue_count: 12 },
];

const CATEGORIES = ["All", "Images", "Buttons", "Links", "Forms", "Headings", "Tables", "ARIA", "Keyboard", "Focus", "Color", "Navigation", "Language", "Meta", "Media", "Zoom", "Other"];
const SEVERITIES = ["All", "critical", "high", "medium", "low"];
const BROWSERS = ["chromium", "firefox", "webkit"];
const STANDARDS = ["WCAG22", "WCAG21", "WCAG20", "Section508", "ADA", "EN301549"];
const SCAN_TYPES = [
  { value: "single_page", label: "Single Page", icon: "📄" },
  { value: "full_site", label: "Full Website", icon: "🌐" },
  { value: "authenticated", label: "Authenticated App", icon: "🔐" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AccessibilityTesting() {
  const [tab, setTab] = useState("dashboard");
  const [scanForm, setScanForm] = useState({ url: "", scan_type: "single_page", browser: "chromium", standard: "WCAG22", project_name: "General", username: "", password: "" });
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [currentScan, setCurrentScan] = useState(null);
  const [scans, setScans] = useState([]);
  const [issues, setIssues] = useState(MOCK_ISSUES);
  const [pages, setPages] = useState(MOCK_PAGES);
  const [dashboard, setDashboard] = useState(MOCK_DASHBOARD);
  const [issueFilter, setIssueFilter] = useState({ severity: "All", category: "All", search: "" });
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [issueCodeTab, setIssueCodeTab] = useState("html");
  const [expandedPages, setExpandedPages] = useState({});
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", text: "👋 Hi! I'm your AI Accessibility Assistant. Ask me about WCAG violations, why elements fail, or how to fix them with code examples." },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [checklistProgress, setChecklistProgress] = useState({});
  const [toast, setToast] = useState(null);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    axios.get(`${API_BASE}/api/accessibility/dashboard`).then(r => setDashboard(r.data)).catch(() => { });
    axios.get(`${API_BASE}/api/accessibility/scans`).then(r => setScans(r.data.scans || [])).catch(() => { });
    axios.get(`${API_BASE}/api/accessibility/checklist`).then(r => setChecklist(r.data.checklist || [])).catch(() => { });
  }, []);

  useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const startPolling = useCallback((scanId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/accessibility/scans/${scanId}`);
        const s = r.data;
        setScanProgress(s.progress || 0);
        setCurrentScan(s);
        if (["completed", "failed", "stopped"].includes(s.status)) {
          clearInterval(pollRef.current);
          setScanning(false);
          if (s.status === "completed") {
            const total = (s.critical_count || 0) + (s.high_count || 0) + (s.medium_count || 0) + (s.low_count || 0);
            showToast(`✅ Scan complete! Found ${total} issues.`);
            const [iRes, pRes, dRes, sRes] = await Promise.all([
              axios.get(`${API_BASE}/api/accessibility/scans/${scanId}/issues?limit=200`),
              axios.get(`${API_BASE}/api/accessibility/scans/${scanId}/pages`),
              axios.get(`${API_BASE}/api/accessibility/dashboard`),
              axios.get(`${API_BASE}/api/accessibility/scans`),
            ]);
            setIssues(iRes.data.issues || MOCK_ISSUES);
            setPages(pRes.data.pages || MOCK_PAGES);
            setDashboard(dRes.data);
            setScans(sRes.data.scans || []);
            setTab("issues");
          }
        }
      } catch { clearInterval(pollRef.current); setScanning(false); }
    }, 1500);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleDownloadPDF = () => {
    const scanId = currentScan?.scan_id;
    if (!scanId) {
      showToast("Invalid scan ID. Cannot generate PDF.", "error");
      return;
    }
    // Directly trigger file download stream from backend with .pdf URL
    window.location.href = `${API_BASE}/api/accessibility/scans/${scanId}/accessibility_report.pdf`;
  };

  const handleDownloadHTML = () => {
    const scanId = currentScan?.scan_id;
    if (!scanId) {
      showToast("Invalid scan ID. Cannot generate HTML.", "error");
      return;
    }
    window.location.href = `${API_BASE}/api/accessibility/scans/${scanId}/accessibility_report.html`;
  };

  const handleDownloadCSV = () => {
    const scanId = currentScan?.scan_id;
    if (!scanId) {
      showToast("Invalid scan ID. Cannot generate CSV.", "error");
      return;
    }
    window.location.href = `${API_BASE}/api/accessibility/scans/${scanId}/accessibility_report.csv`;
  };

  const handleDownloadJSON = () => {
    const scanId = currentScan?.scan_id;
    if (!scanId) {
      showToast("Invalid scan ID. Cannot generate JSON.", "error");
      return;
    }
    window.location.href = `${API_BASE}/api/accessibility/scans/${scanId}/accessibility_report.json`;
  };

  const handleDownloadJUnit = () => {
    const scanId = currentScan?.scan_id;
    if (!scanId) {
      showToast("Invalid scan ID. Cannot generate JUnit XML.", "error");
      return;
    }
    window.location.href = `${API_BASE}/api/accessibility/scans/${scanId}/accessibility_report.xml`;
  };

  const handleDownloadReport = (format) => {
    if (!issues || issues.length === 0) {
      showToast("No issues found to export.", "warning");
      return;
    }
    showToast(`📥 ${format} report generation started!`, "info");

    if (format === "PDF") {
      handleDownloadPDF();
    } else if (format === "HTML") {
      handleDownloadHTML();
    } else if (format === "CSV") {
      handleDownloadCSV();
    } else if (format === "JSON") {
      handleDownloadJSON();
    } else if (format === "JUnit XML") {
      handleDownloadJUnit();
    }
  };

  const handleStartScan = async () => {
    if (!scanForm.url.trim()) { showToast("Please enter a URL to scan.", "error"); return; }
    setScanning(true); setScanProgress(0);
    try {
      const r = await axios.post(`${API_BASE}/api/accessibility/scans`, scanForm);
      setCurrentScan({ scan_id: r.data.scan_id, status: "pending", ...scanForm });
      startPolling(r.data.scan_id);
      showToast("🚀 Scan started! Analysing accessibility...", "info");
    } catch {
      let p = 0;
      const sim = setInterval(() => {
        p += Math.random() * 12 + 5;
        setScanProgress(Math.min(p, 99));
        if (p >= 100) {
          clearInterval(sim); setScanning(false); setScanProgress(100);
          setIssues(MOCK_ISSUES); setPages(MOCK_PAGES); setDashboard(MOCK_DASHBOARD);
          showToast("✅ Scan complete! (Demo mode — 6 issues found)");
          setTab("issues");
        }
      }, 400);
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    setChatMessages(m => [...m, { role: "user", text: userMsg }]);
    setChatLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/api/accessibility/ai-chat`, { message: userMsg, scan_id: currentScan?.scan_id });
      setChatMessages(m => [...m, { role: "assistant", text: r.data.response }]);
    } catch {
      const msg = userMsg.toLowerCase();
      let resp = "I can help you understand accessibility issues and how to fix them. Ask 'Why did this fail?' or 'Show me the HTML fix'.";
      if (msg.includes("why") || msg.includes("fail")) resp = "This element fails because it lacks the required attribute or structure that assistive technologies need. Apply the recommended WCAG fix and re-test with a screen reader.";
      else if (msg.includes("fix") || msg.includes("how")) resp = "To fix: 1) Apply the code fix shown in the Issues panel, 2) Test with a screen reader (NVDA on Windows, VoiceOver on Mac), 3) Re-run the accessibility scan to confirm resolution.";
      else if (msg.includes("wcag")) resp = "WCAG 2.2 has four principles: Perceivable, Operable, Understandable, and Robust. Success criteria are rated Level A (minimum), AA (standard), and AAA (enhanced). Most organisations target WCAG 2.1 Level AA.";
      else if (msg.includes("contrast")) resp = "Color contrast ratio measures luminance difference between text and background. WCAG AA requires 4.5:1 for normal text, 3:1 for large text (18pt+). Use the WebAIM Contrast Checker to verify your palette.";
      setChatMessages(m => [...m, { role: "assistant", text: resp }]);
    }
    setChatLoading(false);
  };

  const filteredIssues = issues.filter(i => {
    if (issueFilter.severity !== "All" && i.severity !== issueFilter.severity) return false;
    if (issueFilter.category !== "All" && i.category !== issueFilter.category) return false;
    if (issueFilter.search && !i.rule_name.toLowerCase().includes(issueFilter.search.toLowerCase()) &&
      !i.description.toLowerCase().includes(issueFilter.search.toLowerCase())) return false;
    return true;
  });

  const TABS = [
    { id: "dashboard", icon: "📊", label: "Dashboard" },
    { id: "scanner", icon: "🔍", label: "Scanner" },
    { id: "issues", icon: "⚠️", label: "Issues", count: filteredIssues.length },
    { id: "pages", icon: "📄", label: "Pages", count: pages.length },
    { id: "score", icon: "🏆", label: "Score" },
    // { id: "chat",      icon: "🤖", label: "AI Chat" },
    // { id: "checklist", icon: "✅", label: "Checklist" },
    { id: "reports", icon: "📋", label: "Reports" },
  ];

  // Shared section heading style
  const SH = { fontSize: 15, fontWeight: 700, color: C.textMain, margin: "0 0 16px" };
  const cardStyle = { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24, boxShadow: C.shadowCard };

  return (
    <div style={{ background: C.pageBg, minHeight: "100vh", color: C.textMain, fontFamily: "var(--font-sans, system-ui, sans-serif)", padding: "28px 32px 48px" }}>

      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        .a11y-fade { animation: fadeIn 0.25s ease; }
        .a11y-spin { animation: spin 1s linear infinite; }
        .a11y-pulse { animation: pulse 1.6s ease-in-out infinite; }
        .a11y-row:hover { background: ${C.cardHover} !important; }
        .a11y-btn:hover { filter: brightness(0.94); }
      `}</style>

      {/* Toast */}
      {toast && (
        <div className="a11y-fade" style={{
          position: "fixed", top: 24, right: 24, zIndex: 9999,
          background: toast.type === "error" ? C.criticalBg : toast.type === "info" ? C.primaryBg : C.passedBg,
          border: `1px solid ${toast.type === "error" ? C.criticalBdr : toast.type === "info" ? "#c4b5fd" : C.passedBdr}`,
          color: toast.type === "error" ? C.critical : toast.type === "info" ? C.primary : C.passed,
          borderRadius: 14, padding: "14px 20px", maxWidth: 400,
          boxShadow: C.shadowLg, fontSize: 14, fontWeight: 600,
        }}>{toast.msg}</div>
      )}

      {/* ── PAGE HEADER ─────────────────────────────────────────────────────── */}
      <div style={{
        background: "#ffffff",
        border: `1px solid ${C.border}`, borderRadius: 20, padding: "24px 28px",
        marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            background: "#FD6101", color: "#fff",
            width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, boxShadow: "0 4px 14px rgba(253, 97, 1, 0.3)",
          }}>♿</div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: C.textMain, letterSpacing: -0.5 }}>
              Accessibility Testing
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textMuted, fontWeight: 500 }}>
              WCAG 2.2 · Automated + AI-Powered · axe-core Engine
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{
            background: C.passedBg, border: `1px solid ${C.passedBdr}`, borderRadius: 10,
            padding: "8px 16px", fontSize: 13, color: C.passed, fontWeight: 600,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.passed, display: "inline-block" }} />
            Engine Ready
          </div>
          <button className="a11y-btn" onClick={() => setTab("scanner")} style={{
            background: "#FD6101", border: "none", borderRadius: 12,
            padding: "10px 22px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
            boxShadow: "0 6px 20px rgba(253, 97, 1, 0.3)",
          }}>+ New Scan</button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: 24 }}>
        <TabBar tabs={TABS} active={tab} onChange={setTab} />
      </div>

      {/* ── DASHBOARD ───────────────────────────────────────────────────────── */}
      {tab === "dashboard" && (
        <div className="a11y-fade">
          {/* Score + Severity row */}
          <div style={{ display: "grid", gridTemplateColumns: "230px 1fr", gap: 20, marginBottom: 20 }}>
            {/* Score card */}
            <div style={{ ...cardStyle, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              <h3 style={{ ...SH, alignSelf: "flex-start" }}>Overall Score</h3>
              <ScoreRing score={dashboard.overall_score} size={130} stroke={11} />
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
                <ProgressBar value={dashboard.wcag_a_pct} color={C.passed} label="WCAG A" />
                <ProgressBar value={dashboard.wcag_aa_pct} color={C.primary} label="WCAG AA" />
                <ProgressBar value={dashboard.wcag_aaa_pct} color={C.accent} label="WCAG AAA" />
              </div>
            </div>
            {/* Severity grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14 }}>
              <MetricCard icon="🚨" label="Critical" value={dashboard.critical_count} color={C.critical} colorBg={C.criticalBg} sub="Immediate action" />
              <MetricCard icon="⚡" label="High" value={dashboard.high_count} color={C.high} colorBg={C.highBg} sub="Fix this sprint" />
              <MetricCard icon="⚠️" label="Medium" value={dashboard.medium_count} color={C.medium} colorBg={C.mediumBg} sub="Planned backlog" />
              <MetricCard icon="✓" label="Low" value={dashboard.low_count} color={C.low} colorBg={C.lowBg} sub="Nice to fix" />
              <MetricCard icon="✅" label="Passed" value={dashboard.passed_count} color={C.passed} colorBg={C.passedBg} sub="Conformant" />
              <MetricCard icon="🔍" label="Total Scans" value={dashboard.total_scans} color={C.primary} colorBg={C.primaryBg} sub="All time" />
            </div>
          </div>

          {/* Trend + Category Charts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div style={cardStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ ...SH, margin: 0 }}>📈 Score Trend</h3>
                <span style={{ fontSize: 12, color: C.textLight }}>Last 7 days</span>
              </div>
              <TrendLine data={dashboard.trend} height={110} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                {dashboard.trend.map((d, i) => (
                  <div key={i} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: C.textLight }}>{d.date?.slice(5)}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(d.score) }}>{d.score}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={cardStyle}>
              <h3 style={SH}>🎯 Category Scores</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {Object.entries(dashboard.category_scores).map(([cat, score]) => (
                  <ProgressBar key={cat} value={score} color={scoreColor(score)} label={cat} />
                ))}
              </div>
            </div>
          </div>

          {/* Bar chart + Top violations */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 20 }}>
            <div style={cardStyle}>
              <h3 style={SH}>📊 Issues by Category</h3>
              <BarChart height={220} data={
                dashboard.top_violations.map(v => ({
                  label: v.category,
                  value: v.count,
                  color: v.severity === "critical" ? C.critical : v.severity === "high" ? C.high : C.medium
                }))
              } />
            </div>
            <div style={cardStyle}>
              <h3 style={SH}>🏆 Most Common Violations</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {dashboard.top_violations.slice(0, 8).map((v, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: C.pageBg, borderRadius: 10, border: `1px solid ${C.borderLight}` }}>
                    <span style={{ fontSize: 12, color: C.textLight, minWidth: 22, fontWeight: 700 }}>#{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.textMain, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.rule}</div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>{v.category}</div>
                    </div>
                    <SevBadge sev={v.severity} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: SEV[v.severity]?.color || C.primary, minWidth: 28, textAlign: "right" }}>{v.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SCANNER ─────────────────────────────────────────────────────────── */}
      {tab === "scanner" && (
        <div className="a11y-fade" style={{ maxWidth: 780 }}>
          <div style={cardStyle}>
            <div style={{
              background: "#fff7ed",
              border: "1px solid #fed7aa", borderRadius: 14, padding: "20px 24px", marginBottom: 28,
            }}>
              <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800, color: C.textMain }}>🔍 New Accessibility Scan</h2>
              <p style={{ margin: 0, color: C.textMuted, fontSize: 13 }}>Configure your scan settings and start automated WCAG compliance analysis.</p>
            </div>

            {/* URL */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Target URL</label>
              <input type="url" placeholder="https://example.com" value={scanForm.url}
                onChange={e => setScanForm(f => ({ ...f, url: e.target.value }))}
                style={{ width: "100%", padding: "12px 16px", borderRadius: 12, fontSize: 14, background: C.pageBg, border: `1.5px solid ${C.border}`, color: C.textMain, outline: "none", transition: "border-color 0.2s" }}
                onFocus={e => e.target.style.borderColor = C.primary}
                onBlur={e => e.target.style.borderColor = C.border} />
            </div>

            {/* Scan type */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Scan Type</label>
              <div style={{ display: "flex", gap: 10 }}>
                {SCAN_TYPES.map(t => (
                  <button key={t.value} onClick={() => setScanForm(f => ({ ...f, scan_type: t.value }))} style={{
                    flex: 1, padding: "12px 12px", borderRadius: 12, cursor: "pointer",
                    background: scanForm.scan_type === t.value ? C.primaryBg : C.pageBg,
                    border: `1.5px solid ${scanForm.scan_type === t.value ? C.primary : C.border}`,
                    color: scanForm.scan_type === t.value ? C.primary : C.textMuted,
                    fontWeight: 600, fontSize: 13, transition: "all 0.2s",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  }}>
                    <span style={{ fontSize: 20 }}>{t.icon}</span><span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Authentication Credentials (Optional) */}
            <div style={{ background: "rgba(124, 58, 237, 0.03)", border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: C.primary, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
                  🔑 Authentication (Optional)
                </span>
                {scanForm.url && (
                  scanForm.url.toLowerCase().includes("login") ||
                  scanForm.url.toLowerCase().includes("signin") ||
                  scanForm.url.toLowerCase().includes("auth") ||
                  scanForm.url.toLowerCase().includes("signup")
                ) && (
                    <span style={{ fontSize: 11, color: C.textMuted, background: C.borderLight, padding: "3px 10px", borderRadius: 20, fontWeight: 700 }}>
                      Detected Login Page
                    </span>
                  )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Username / Email</label>
                  <input type="text" placeholder="e.g. admin@example.com" value={scanForm.username || ""}
                    onChange={e => setScanForm(f => ({ ...f, username: e.target.value }))}
                    style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: C.cardBg, border: `1.5px solid ${C.border}`, color: C.textMain, fontSize: 13, outline: "none" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Password</label>
                  <input type="password" placeholder="••••••••" value={scanForm.password || ""}
                    onChange={e => setScanForm(f => ({ ...f, password: e.target.value }))}
                    style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: C.cardBg, border: `1.5px solid ${C.border}`, color: C.textMain, fontSize: 13, outline: "none" }} />
                </div>
              </div>
            </div>

            {/* Browser + Standard */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Browser</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {BROWSERS.map(b => (
                    <button key={b} onClick={() => setScanForm(f => ({ ...f, browser: b }))} style={{
                      flex: 1, padding: "10px 6px", borderRadius: 10, cursor: "pointer",
                      background: scanForm.browser === b ? C.primaryBg : C.pageBg,
                      border: `1.5px solid ${scanForm.browser === b ? C.primary : C.border}`,
                      color: scanForm.browser === b ? C.primary : C.textMuted,
                      fontWeight: 600, fontSize: 12, transition: "all 0.2s", textTransform: "capitalize",
                    }}>{b}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Standard</label>
                <select value={scanForm.standard} onChange={e => setScanForm(f => ({ ...f, standard: e.target.value }))}
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 10, background: C.pageBg, border: `1.5px solid ${C.border}`, color: C.textMain, fontSize: 13, cursor: "pointer", outline: "none" }}>
                  {STANDARDS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Project */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Project Name</label>
              <input type="text" placeholder="My Project" value={scanForm.project_name}
                onChange={e => setScanForm(f => ({ ...f, project_name: e.target.value }))}
                style={{ width: "100%", padding: "11px 14px", borderRadius: 10, background: C.pageBg, border: `1.5px solid ${C.border}`, color: C.textMain, fontSize: 13, outline: "none" }} />
            </div>

            {/* Progress */}
            {scanning && (
              <div style={{ background: C.pageBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.textMain }}>
                    <span className="a11y-spin" style={{ display: "inline-block", marginRight: 8 }}>⚙️</span>
                    Scanning {scanForm.url}...
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: C.primary }}>{Math.round(scanProgress)}%</span>
                </div>
                <div style={{ background: C.borderLight, borderRadius: 20, height: 10, overflow: "hidden" }}>
                  <div style={{ width: `${scanProgress}%`, height: "100%", borderRadius: 20, background: "#FD6101", transition: "width 0.4s ease" }} />
                </div>
                <div style={{ display: "flex", gap: 20, marginTop: 10, fontSize: 12, color: C.textLight }}>
                  <span>🌐 Crawling pages…</span><span>🔍 Analysing DOM…</span><span>♿ Checking WCAG rules…</span>
                </div>
              </div>
            )}

            <button className="a11y-btn" onClick={handleStartScan} disabled={scanning} style={{
              width: "100%", padding: 16, borderRadius: 12, border: "none", cursor: scanning ? "not-allowed" : "pointer",
              background: scanning ? C.borderLight : "#FD6101",
              color: scanning ? C.textMuted : "#fff", fontWeight: 800, fontSize: 16,
              boxShadow: scanning ? "none" : "0 6px 20px rgba(253, 97, 1, 0.3)",
            }}>
              {scanning ? "⏳ Scanning…" : "🚀 Start Accessibility Scan"}
            </button>
          </div>

          {/* Recent scans */}
          {scans.length > 0 && (
            <div style={{ ...cardStyle, marginTop: 20 }}>
              <h3 style={SH}>📋 Recent Scans</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {scans.slice(0, 5).map(s => (
                  <div key={s.scan_id} className="a11y-row" onClick={() => { setCurrentScan(s); setTab("issues"); }}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: C.pageBg, border: `1px solid ${C.borderLight}`, borderRadius: 12, cursor: "pointer", transition: "background 0.15s" }}>
                    <ScoreRing score={s.overall_score || 0} size={44} stroke={5} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.textMain }}>{s.url}</div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>{s.standard} · {s.browser} · {s.pages_scanned} pages</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{
                        fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 700,
                        background: s.status === "completed" ? C.passedBg : C.primaryBg,
                        color: s.status === "completed" ? C.passed : C.primary
                      }}>{s.status}</div>
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{s.created_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ISSUES ──────────────────────────────────────────────────────────── */}
      {tab === "issues" && (
        <div className="a11y-fade">
          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <input type="text" placeholder="🔍 Search issues…" value={issueFilter.search}
              onChange={e => setIssueFilter(f => ({ ...f, search: e.target.value }))}
              style={{ padding: "10px 14px", borderRadius: 10, background: C.cardBg, border: `1px solid ${C.border}`, color: C.textMain, fontSize: 13, flex: 1, minWidth: 200, outline: "none" }} />
            <select value={issueFilter.severity} onChange={e => setIssueFilter(f => ({ ...f, severity: e.target.value }))}
              style={{ padding: "10px 14px", borderRadius: 10, background: C.cardBg, border: `1px solid ${C.border}`, color: C.textMain, fontSize: 13, cursor: "pointer", outline: "none" }}>
              {SEVERITIES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={issueFilter.category} onChange={e => setIssueFilter(f => ({ ...f, category: e.target.value }))}
              style={{ padding: "10px 14px", borderRadius: 10, background: C.cardBg, border: `1px solid ${C.border}`, color: C.textMain, fontSize: 13, cursor: "pointer", outline: "none" }}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <span style={{ fontSize: 13, color: C.textMuted, fontWeight: 500, whiteSpace: "nowrap" }}>
              {filteredIssues.length} issue{filteredIssues.length !== 1 ? "s" : ""}
            </span>
          </div>

          {filteredIssues.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", padding: "80px 20px" }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.textMain, marginBottom: 8 }}>No Issues Found</div>
              <div style={{ fontSize: 14, color: C.textMuted }}>Great job! No accessibility issues match your current filters.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredIssues.map(issue => {
                const sev = SEV[issue.severity] || SEV.low;
                const isOpen = expandedIssue === issue.id;
                return (
                  <div key={issue.id} style={{
                    background: C.cardBg, border: `1px solid ${isOpen ? C.primary : C.border}`,
                    borderRadius: 14, overflow: "hidden", boxShadow: isOpen ? `0 0 0 3px ${C.primaryBg}` : C.shadowCard,
                    transition: "all 0.2s",
                  }}>
                    {/* Row */}
                    <div className="a11y-row" onClick={() => setExpandedIssue(isOpen ? null : issue.id)}
                      style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", cursor: "pointer" }}>
                      <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: sev.color, minHeight: 36, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: C.textMain }}>{issue.rule_name}</span>
                          <SevBadge sev={issue.severity} />
                          <span style={{ fontSize: 11, color: C.textMuted, background: C.pageBg, padding: "2px 8px", borderRadius: 20, border: `1px solid ${C.border}`, fontWeight: 600 }}>
                            WCAG {issue.wcag_criterion}
                          </span>
                          <span style={{ fontSize: 11, color: C.primary, background: C.primaryBg, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
                            {issue.category}
                          </span>
                          {(() => {
                            const pUrl = issue.page_url || "";
                            let prettyName = "Home";
                            if (pUrl.includes("#/")) {
                              prettyName = pUrl.split("#/")[1].split("?")[0].replace(/-/g, " ").replace(/_/g, " ");
                            } else {
                              prettyName = pUrl.split("/").pop().split("?")[0].replace(/-/g, " ").replace(/_/g, " ") || "Home";
                            }
                            prettyName = prettyName.replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
                            return (
                              <span style={{ fontSize: 11, color: "#6366f1", background: "rgba(99, 102, 241, 0.08)", padding: "2px 8px", borderRadius: 20, fontWeight: 700, border: "1px solid rgba(99, 102, 241, 0.15)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                📍 Screen: {prettyName}
                              </span>
                            );
                          })()}
                        </div>
                        <div style={{ fontSize: 13, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {issue.description}
                        </div>
                      </div>
                      <div style={{ textAlign: "center", flexShrink: 0, marginLeft: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.medium }}>~{issue.est_fix_minutes}m</div>
                        <div style={{ fontSize: 10, color: C.textLight }}>fix time</div>
                      </div>
                      <span style={{ color: C.textLight, fontSize: 16 }}>{isOpen ? "▲" : "▼"}</span>
                    </div>

                    {/* Expanded */}
                    {isOpen && (
                      <div className="a11y-fade" style={{ borderTop: `1px solid ${C.borderLight}`, padding: "20px 24px", background: C.pageBg }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                          {/* Left */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div style={{ background: C.criticalBg, border: `1px solid ${C.criticalBdr}`, borderRadius: 10, padding: 14 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.critical, marginBottom: 6 }}>⚠️ WHY IT FAILED</div>
                              <div style={{ fontSize: 13, color: C.textMain, lineHeight: 1.6 }}>{issue.why_failed}</div>
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                              {[["IMPACT", issue.impact], ["AFFECTED USERS", issue.affected_users]].map(([l, v]) => (
                                <div key={l} style={{ flex: 1, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
                                  <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, marginBottom: 4 }}>{l}</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: C.textMain, lineHeight: 1.4 }}>{v}</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ background: C.passedBg, border: `1px solid ${C.passedBdr}`, borderRadius: 10, padding: 14 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.passed, marginBottom: 6 }}>💡 FIX SUGGESTION</div>
                              <div style={{ fontSize: 13, color: C.textMain, lineHeight: 1.6 }}>{issue.fix_suggestion}</div>
                            </div>
                            <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>🎯 ELEMENT LOCATION</div>
                              {[["XPath", issue.element_xpath], ["CSS", issue.element_css], ["Page", issue.page_url]].map(([l, v]) => (
                                <div key={l} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 12 }}>
                                  <span style={{ color: C.textLight, minWidth: 40, fontWeight: 600 }}>{l}:</span>
                                  <span style={{ color: C.primary, fontFamily: "monospace", wordBreak: "break-all" }}>{v}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* Right */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {(() => {
                              const matchedPage = pages.find(p => p.url === issue.page_url);
                              if (matchedPage && matchedPage.screenshot) {
                                return (
                                  <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: C.cardBg }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, padding: "6px 12px", background: C.pageBg, borderBottom: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", gap: 6 }}>
                                      🖥️ SCREEN PREVIEW
                                    </div>
                                    <div style={{ padding: 8, display: "flex", justifyContent: "center", background: "#f8fafc" }}>
                                      <img
                                        src={`data:image/png;base64,${matchedPage.screenshot}`}
                                        alt="Page Screenshot Preview"
                                        style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 6, border: `1px solid ${C.borderLight}` }}
                                      />
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>📄 SOURCE SNIPPET</div>
                              <CodeBlock code={issue.source_snippet} language="html" />
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>🔧 CORRECTED CODE</div>
                              <TabBar
                                tabs={[{ id: "html", icon: "", label: "HTML" }, { id: "react", icon: "", label: "React" }, { id: "angular", icon: "", label: "Angular" }, { id: "vue", icon: "", label: "Vue" }]}
                                active={issueCodeTab} onChange={setIssueCodeTab} />
                              <div style={{ marginTop: 8 }}>
                                <CodeBlock
                                  code={issueCodeTab === "html" ? issue.html_fix : issueCodeTab === "react" ? issue.react_fix : issueCodeTab === "angular" ? issue.angular_fix : issue.vue_fix}
                                  language={issueCodeTab} />
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Actions */}
                        <div style={{ display: "flex", gap: 10, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                          {[
                            { label: "🤖 Ask AI", action: () => { setTab("chat"); setChatInput(`How do I fix "${issue.rule_name}"?`); }, color: C.primary, bg: C.primaryBg },
                            { label: "🎫 Create Jira Ticket", action: () => showToast("Jira — configure in Settings.", "info"), color: "#0052cc", bg: "#e8f0ff" },
                            { label: "✓ Mark Resolved", action: () => showToast("Issue marked as resolved."), color: C.passed, bg: C.passedBg },
                          ].map(({ label, action, color, bg }) => (
                            <button key={label} className="a11y-btn" onClick={action} style={{
                              padding: "8px 16px", borderRadius: 8, border: `1px solid ${color}33`,
                              background: bg, color, fontWeight: 700, fontSize: 13, cursor: "pointer",
                            }}>{label}</button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PAGE EXPLORER ────────────────────────────────────────────────────── */}
      {tab === "pages" && (
        <div className="a11y-fade">
          {pages.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.textMain, marginBottom: 6 }}>No pages scanned yet</div>
              <div style={{ fontSize: 13, color: C.textMuted }}>Run a scan from the Scanner tab to see pages here.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {pages.map(page => (
                <div key={page.id} style={{ background: C.cardBg, border: `1px solid ${expandedPages[page.id] ? C.primary : C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: C.shadowCard, transition: "all 0.2s" }}>
                  <div className="a11y-row" onClick={() => setExpandedPages(p => ({ ...p, [page.id]: !p[page.id] }))}
                    style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", cursor: "pointer" }}>
                    <ScoreRing score={page.score} size={50} stroke={5} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.textMain, marginBottom: 2 }}>{page.title || page.url}</div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>{page.url}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: page.issue_count > 20 ? C.critical : page.issue_count > 10 ? C.high : page.issue_count > 5 ? C.medium : C.low }}>{page.issue_count}</div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>Issues</div>
                    </div>
                    <div style={{ background: C.passedBg, border: `1px solid ${C.passedBdr}`, color: C.passed, borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>✓ Scanned</div>
                    <span style={{ color: C.textLight, fontSize: 15 }}>{expandedPages[page.id] ? "▲" : "▼"}</span>
                  </div>
                  {expandedPages[page.id] && (
                    <div className="a11y-fade" style={{ borderTop: `1px solid ${C.borderLight}`, padding: "16px 20px", background: C.pageBg }}>
                      <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 10 }}>
                        Issues for <span style={{ color: C.primary, fontWeight: 600 }}>{page.url}</span>
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {issues.filter(i => i.page_url === page.url || !i.page_url).slice(0, 4).map(i => (
                          <div key={i.id} style={{ background: C.cardBg, border: `1px solid ${SEV[i.severity]?.border || C.border}`, borderRadius: 8, padding: "7px 12px" }}>
                            <SevBadge sev={i.severity} /> <span style={{ fontSize: 12, color: C.textMain, marginLeft: 6 }}>{i.rule_name}</span>
                          </div>
                        ))}
                        <button className="a11y-btn" onClick={() => setTab("issues")} style={{
                          background: C.primaryBg, color: C.primary, border: `1px solid ${C.primary}33`,
                          borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}>View all {page.issue_count} issues →</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── SCORE CARD ──────────────────────────────────────────────────────── */}
      {tab === "score" && (
        <div className="a11y-fade">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ ...cardStyle, display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
              <h2 style={{ ...SH, alignSelf: "flex-start" }}>🏆 Overall Accessibility Score</h2>
              <ScoreRing score={dashboard.overall_score} size={170} stroke={14} />
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { label: "WCAG 2.2 Level A", val: dashboard.wcag_a_pct, color: C.passed },
                  { label: "WCAG 2.2 Level AA", val: dashboard.wcag_aa_pct, color: C.primary },
                  { label: "WCAG 2.2 Level AAA", val: dashboard.wcag_aaa_pct, color: C.accent },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ background: C.pageBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 600, color: C.textMain }}>{label}</span>
                      <span style={{ fontWeight: 800, color }}>{val}%</span>
                    </div>
                    <ProgressBar value={val} color={color} />
                  </div>
                ))}
              </div>
            </div>
            <div style={cardStyle}>
              <h2 style={SH}>📊 Category Breakdown</h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                {Object.entries(dashboard.category_scores).map(([cat, score]) => (
                  <div key={cat} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "12px 8px", background: C.pageBg, borderRadius: 12, border: `1px solid ${C.borderLight}` }}>
                    <ScoreRing score={score} size={68} stroke={6} />
                    <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 600, textAlign: "center" }}>{cat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Severity summary */}
          <div style={{ ...cardStyle, marginTop: 20 }}>
            <h3 style={SH}>📈 Severity Summary</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
              {[
                { sev: "critical", count: dashboard.critical_count, label: "Critical Issues" },
                { sev: "high", count: dashboard.high_count, label: "High Issues" },
                { sev: "medium", count: dashboard.medium_count, label: "Medium Issues" },
                { sev: "low", count: dashboard.low_count, label: "Low Issues" },
              ].map(({ sev, count, label }) => {
                const s = SEV[sev];
                const total = (dashboard.critical_count || 0) + (dashboard.high_count || 0) + (dashboard.medium_count || 0) + (dashboard.low_count || 0);
                const pct = total ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={sev} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 14, padding: 18 }}>
                    <div style={{ fontSize: 30, fontWeight: 900, color: s.color, marginBottom: 4 }}>{fmt(count)}</div>
                    <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 10 }}>{label}</div>
                    <ProgressBar value={pct} color={s.color} />
                    <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{pct}% of total</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── AI CHAT ─────────────────────────────────────────────────────────── */}
      {tab === "chat" && (
        <div className="a11y-fade" style={{ maxWidth: 820, display: "flex", flexDirection: "column", height: "calc(100vh - 280px)", minHeight: 480 }}>
          {/* Quick questions */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {["Why did this fail?", "How do I fix this?", "Show corrected HTML", "Explain WCAG 1.1.1", "Show React example", "What is ARIA?", "How to fix color contrast?"].map(q => (
              <button key={q} className="a11y-btn" onClick={() => setChatInput(q)} style={{
                padding: "7px 14px", borderRadius: 20, border: `1px solid ${C.border}`,
                background: C.cardBg, color: C.textMuted, fontSize: 12, cursor: "pointer", fontWeight: 500,
                boxShadow: C.shadow, transition: "all 0.15s",
              }}>{q}</button>
            ))}
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto", background: C.cardBg, border: `1px solid ${C.border}`,
            borderRadius: "16px 16px 0 0", padding: 20, display: "flex", flexDirection: "column", gap: 14,
          }}>
            {chatMessages.map((msg, i) => (
              <div key={i} className="a11y-fade" style={{ display: "flex", gap: 10, flexDirection: msg.role === "user" ? "row-reverse" : "row" }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: msg.role === "user" ? C.primaryBg : "#002247",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  border: `1px solid ${msg.role === "user" ? C.primary + "33" : "transparent"}`,
                }}>{msg.role === "user" ? "👤" : "🤖"}</div>
                <div style={{
                  background: msg.role === "user" ? C.primaryBg : C.pageBg,
                  border: `1px solid ${msg.role === "user" ? C.primary + "33" : C.border}`,
                  borderRadius: 14, padding: "12px 16px", maxWidth: "80%",
                  fontSize: 14, lineHeight: 1.7, color: C.textMain,
                }}>{msg.text}</div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#002247", display: "flex", alignItems: "center", justifyContent: "center" }}>🤖</div>
                <div className="a11y-pulse" style={{ background: C.pageBg, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 20px", color: C.textMuted, fontSize: 14 }}>
                  Thinking… ●●●
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 16px 16px", padding: 12, display: "flex", gap: 10 }}>
            <input value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleChatSend()}
              placeholder="Ask about any accessibility issue, WCAG rule, or fix…"
              style={{ flex: 1, padding: "11px 16px", borderRadius: 10, background: C.pageBg, border: `1px solid ${C.border}`, color: C.textMain, fontSize: 14, outline: "none" }} />
            <button className="a11y-btn" onClick={handleChatSend} disabled={chatLoading} style={{
              padding: "11px 24px", borderRadius: 10, border: "none", cursor: "pointer",
              background: "#FD6101", color: "#fff", fontWeight: 700, fontSize: 14,
              boxShadow: "0 4px 14px rgba(253, 97, 1, 0.3)",
            }}>Send</button>
          </div>
        </div>
      )}

      {/* ── CHECKLIST ───────────────────────────────────────────────────────── */}
      {tab === "checklist" && (
        <div className="a11y-fade">
          {checklist.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ ...SH, margin: 0 }}>📋 Manual Test Progress</h3>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.primary }}>
                  {Object.values(checklistProgress).filter(Boolean).length} / {checklist.length}
                </span>
              </div>
              <ProgressBar value={Math.round(Object.values(checklistProgress).filter(Boolean).length / checklist.length * 100)} color={C.primary} />
            </div>
          )}

          {["Keyboard", "Focus", "Screen Reader", "Forms", "Zoom", "Color", "Media", "Navigation", "Language", "Timing", "Motion", "Modals", "Touch"].map(cat => {
            const items = checklist.filter(c => c.category === cat);
            if (!items.length) return null;
            return (
              <div key={cat} style={{ ...cardStyle, marginBottom: 14 }}>
                <h3 style={{ ...SH, color: C.primary }}>{cat}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {items.map(item => (
                    <label key={item.id} style={{
                      display: "flex", alignItems: "center", gap: 14, padding: "10px 14px",
                      background: checklistProgress[item.id] ? C.passedBg : C.pageBg,
                      border: `1px solid ${checklistProgress[item.id] ? C.passedBdr : C.borderLight}`,
                      borderRadius: 10, cursor: "pointer", transition: "all 0.15s",
                    }}>
                      <input type="checkbox" checked={!!checklistProgress[item.id]}
                        onChange={() => setChecklistProgress(p => ({ ...p, [item.id]: !p[item.id] }))}
                        style={{ width: 17, height: 17, cursor: "pointer", accentColor: C.primary }} />
                      <div style={{
                        flex: 1, fontSize: 14, fontWeight: checklistProgress[item.id] ? 400 : 600,
                        color: checklistProgress[item.id] ? C.textLight : C.textMain,
                        textDecoration: checklistProgress[item.id] ? "line-through" : "none"
                      }}>
                        {item.test}
                      </div>
                      <span style={{ fontSize: 11, color: C.primary, background: C.primaryBg, padding: "2px 8px", borderRadius: 20, fontWeight: 700, whiteSpace: "nowrap" }}>
                        WCAG {item.wcag}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {checklist.length === 0 && (
            <div style={{ ...cardStyle, textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.textMain }}>Loading checklist…</div>
            </div>
          )}
        </div>
      )}

      {/* ── REPORTS ─────────────────────────────────────────────────────────── */}
      {tab === "reports" && (
        <div className="a11y-fade" style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 28, alignItems: "start" }}>
          {/* Left Column: Generate Report */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {/* Premium Exporters Grid */}
            <div style={{ ...cardStyle, borderTop: `4px solid ${C.primary}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: C.textMain, display: "flex", alignItems: "center", gap: 10 }}>
                  <span>📋</span> Generate Compliance Report
                </h3>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: C.primaryBg, color: C.primary, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  WCAG 2.2 Standard
                </span>
              </div>
              <p style={{ margin: "0 0 24px", color: C.textMuted, fontSize: 13, lineHeight: 1.6 }}>
                Export client-ready accessibility audits and compliance certificates. All reports include captured screenshots, DOM selectors, WCAG guidelines, and actionable code fixes.
              </p>
              <h4 style={{ fontSize: 13, fontWeight: 800, margin: "0 0 16px", color: C.textMain, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Select Export Format
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
                {[
                  {
                    fmt: "PDF",
                    icon: "📄",
                    badge: "Highly Recommended",
                    badgeBg: C.criticalBg,
                    badgeClr: C.critical,
                    desc: "Polished client-ready report with charts, violation tables, and screen screenshots.",
                    action: handleDownloadPDF
                  },
                  {
                    fmt: "HTML",
                    icon: "🌐",
                    badge: "Interactive Share",
                    badgeBg: C.primaryBg,
                    badgeClr: C.primary,
                    desc: "Self-contained offline audit page with search filters, screenshots, and source code viewer.",
                    action: handleDownloadHTML
                  },
                  {
                    fmt: "CSV",
                    icon: "📊",
                    badge: "Data Spreadsheet",
                    badgeBg: C.passedBg,
                    badgeClr: C.passed,
                    desc: "Tabular raw violation rows, xpath targets, and fix URLs for Excel import.",
                    action: handleDownloadCSV
                  }
                ].map(({ fmt, icon, badge, badgeBg, badgeClr, desc, action }) => (
                  <div key={fmt} className="a11y-row" style={{
                    background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 16,
                    padding: 20, display: "flex", flexDirection: "column", justifyContent: "space-between",
                    transition: "all 0.25s ease", cursor: "default", height: "100%"
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.boxShadow = "0 8px 16px -4px rgba(0,0,0,0.06)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = "none"; }}>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <span style={{ fontSize: 28 }}>{icon}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 12, background: badgeBg, color: badgeClr, textTransform: "uppercase" }}>
                          {badge}
                        </span>
                      </div>
                      <h4 style={{ fontSize: 18, fontWeight: 800, margin: "0 0 8px", color: C.textMain }}>{fmt} Document</h4>
                      <p style={{ margin: "0 0 16px", fontSize: 12, color: C.textMuted, lineHeight: 1.5, minHeight: 60 }}>{desc}</p>
                    </div>
                    <button className="a11y-btn" onClick={action} style={{
                      width: "100%", padding: "10px", borderRadius: 10, border: "none",
                      background: C.primary, color: "#ffffff", fontWeight: 700, fontSize: 13,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      boxShadow: "0 4px 10px -2px rgba(124, 58, 237, 0.2)", transition: "all 0.2s"
                    }}>
                      <span>📥</span> Download {fmt}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Report Summary & Metrics */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {/* Executive Summary Card */}
            <div style={{ ...cardStyle, borderTop: "4px solid #4f46e5" }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 20px", color: C.textMain, display: "flex", alignItems: "center", gap: 8 }}>
                <span>📊</span> Executive Compliance Scorecard
              </h3>

              {/* Radial Circle Mimic */}
              <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, padding: "12px", background: C.pageBg, borderRadius: 16, border: `1px solid ${C.borderLight}` }}>
                <div style={{
                  width: 72, height: 72, borderRadius: "50%",
                  background: `conic-gradient(${C.primary} ${dashboard.wcag_aa_pct}%, ${C.border} 0)`,
                  display: "flex", alignItems: "center", justifyContent: "center", position: "relative"
                }}>
                  <div style={{
                    width: 58, height: 58, borderRadius: "50%", background: C.cardBg,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: C.primary
                  }}>
                    {dashboard.wcag_aa_pct}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.textMain }}>Level AA Met</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>Compliance rating for target screens</div>
                </div>
              </div>

              {/* Status List */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { std: "Level A: Essential Accessibility", desc: "Basic requirements (image alt texts, keyboard access)", pct: dashboard.wcag_a_pct, pass: dashboard.wcag_a_pct >= 95 },
                  { std: "Level AA: Global Legal Standard", desc: "Target standard (color contrast, text resizing, focus)", pct: dashboard.wcag_aa_pct, pass: dashboard.wcag_aa_pct >= 90 },
                  { std: "Level AAA: Advanced Experience", desc: "Highest standard (optimal accessibility, transcripts)", pct: dashboard.wcag_aaa_pct, pass: dashboard.wcag_aaa_pct >= 80 },
                  { std: "US Section 508 (Federal)", desc: "Legal requirements for government digital content", pct: dashboard.wcag_aa_pct, pass: dashboard.wcag_aa_pct >= 90 },
                  { std: "US ADA Title III (Public Web)", desc: "Civil rights requirements for public digital spaces", pct: dashboard.wcag_aa_pct, pass: dashboard.wcag_aa_pct >= 90 },
                ].map(({ std, desc, pct, pass }) => (
                  <div key={std} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
                    padding: "12px 16px", background: C.pageBg, border: `1px solid ${C.borderLight}`, borderRadius: 12
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.textMain, marginBottom: 2 }}>{std}</div>
                      <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.3 }}>{desc}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, shrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: pass ? C.passed : C.critical }}>{pct}%</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                        background: pass ? C.passedBg : C.criticalBg, color: pass ? C.passed : C.critical,
                        border: `1px solid ${pass ? C.passedBdr : C.criticalBdr}`, textTransform: "uppercase",
                        whiteSpace: "nowrap"
                      }}>
                        {pass ? "Compliant" : "Action Needed"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Checklist */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 16px", color: C.textMain }}>📋 Verification Checklist</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {["Screenshots with highlighted element", "XPath & CSS selectors", "Source code + corrected code", "WCAG criterion reference", "Priority-ordered fix list", "Compliance executive summary"].map(item => (
                  <div key={item} style={{
                    fontSize: 12, color: C.textMain, display: "flex", alignItems: "center", gap: 10,
                    padding: "6px 12px", background: C.pageBg, borderRadius: 8, border: `1px solid ${C.borderLight}`
                  }}>
                    <span style={{ color: C.passed, fontWeight: 800, fontSize: 14 }}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Integrations
            <div style={cardStyle}>
              <h3 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 16px", color: C.textMain }}>🔗 Active Sync Integrations</h3>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { tool: "Jira", color: "#0052cc" },
                  { tool: "Azure DevOps", color: "#0078d4" },
                  { tool: "GitHub Issues", color: "#24292e" },
                  { tool: "GitLab", color: "#fc6d26" },
                  { tool: "Slack", color: "#4a154b" },
                  { tool: "MS Teams", color: "#6264a7" },
                  { tool: "Jenkins", color: "#d24939" }
                ].map(({ tool, color }) => (
                  <button key={tool} className="a11y-btn" onClick={() => showToast(`${tool} integration is synced and ready in configuration!`, "info")} style={{
                    padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
                    background: C.cardBg, color: C.textMain, fontSize: 11, cursor: "pointer", fontWeight: 700,
                    display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s"
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = `${color}08`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.cardBg; }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                    {tool}
                  </button>
                ))}
              </div>
            </div>
            */}
          </div>
        </div>
      )}

      {/* ── REPORTS ──────────────────────────────────────────────────────────── */}
      {tab === "reports" && (
        <div className="a11y-fade" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Executive Download Header */}
          <div style={{ ...cardStyle, background: "#002247", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ background: C.passed, color: "#fff", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800 }}>CERTIFIED EXPORT READY</span>
                <span style={{ fontSize: 12, color: "#a5b4fc" }}>Scan ID: {currentScan?.scan_id || "SCAN-20260728-001"}</span>
              </div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#fff" }}>
                Executive Accessibility & WCAG Compliance Report
              </h2>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>
                Automated axe-core scan findings, WCAG 2.2 Level AA criteria, and developer remediation plan.
              </p>
            </div>

            {/* Download Buttons */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="a11y-btn" onClick={() => handleDownloadReport("PDF")} style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                📥 Export PDF
              </button>
              <button className="a11y-btn" onClick={() => handleDownloadReport("HTML")} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                📄 Open HTML
              </button>
              <button className="a11y-btn" onClick={() => handleDownloadReport("CSV")} style={{ background: "rgba(255,255,255,0.1)", color: "#e2e8f0", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 10, padding: "10px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                📊 CSV
              </button>
              <button className="a11y-btn" onClick={() => handleDownloadReport("JSON")} style={{ background: "rgba(255,255,255,0.1)", color: "#e2e8f0", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 10, padding: "10px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                { "{ }" } JSON
              </button>
            </div>
          </div>

          {/* Compliance Summary & Score Card */}
          <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 20 }}>
            {/* Executive Score Box */}
            <div style={{ ...cardStyle, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>Overall Compliance Score</div>
              <ScoreRing score={dashboard.overall_score} size={140} stroke={12} />
              <div style={{ background: C.primaryBg, color: C.primary, padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 800 }}>
                axe-core Engine v4.8
              </div>
            </div>

            {/* Breakdown Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <div style={{ ...cardStyle, background: C.criticalBg, borderColor: C.criticalBdr }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.critical, textTransform: "uppercase" }}>Critical Violations</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: C.critical, margin: "6px 0" }}>{dashboard.critical_count}</div>
                <div style={{ fontSize: 12, color: C.critical }}>Requires immediate fix for screen readers</div>
              </div>
              <div style={{ ...cardStyle, background: C.highBg, borderColor: C.highBdr }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.high, textTransform: "uppercase" }}>High Severity</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: C.high, margin: "6px 0" }}>{dashboard.high_count}</div>
                <div style={{ fontSize: 12, color: C.high }}>Color contrast & missing form labels</div>
              </div>
              <div style={{ ...cardStyle, background: C.passedBg, borderColor: C.passedBdr }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.passed, textTransform: "uppercase" }}>Conformant Checks</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: C.passed, margin: "6px 0" }}>{dashboard.passed_count}</div>
                <div style={{ fontSize: 12, color: C.passed }}>Passed automated WCAG rules</div>
              </div>
            </div>
          </div>

          {/* Historical Generated Reports Table */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.textMain }}>
                📜 Historical Audit Reports & Artifacts
              </h3>
              <span style={{ fontSize: 12, color: C.textMuted }}>{scans.length || 1} Scans Recorded</span>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.borderLight, textAlign: "left", color: C.textMuted }}>
                  <th style={{ padding: 12 }}>Scan ID</th>
                  <th style={{ padding: 12 }}>Target URL</th>
                  <th style={{ padding: 12 }}>Standard</th>
                  <th style={{ padding: 12 }}>Score</th>
                  <th style={{ padding: 12 }}>Issues</th>
                  <th style={{ padding: 12 }}>Export Reports</th>
                </tr>
              </thead>
              <tbody>
                {(scans.length > 0 ? scans : [
                  { scan_id: "SCAN-20260728-001", url: "https://example.com", standard: "WCAG 2.2 AA", score: 67, total_issues: 116 }
                ]).map((s, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                    <td style={{ padding: 12, fontWeight: 700, fontFamily: "monospace", color: C.primary }}>{s.scan_id}</td>
                    <td style={{ padding: 12, color: C.textMain, fontWeight: 600 }}>{s.url}</td>
                    <td style={{ padding: 12, color: C.textMuted }}>{s.standard || "WCAG 2.2 AA"}</td>
                    <td style={{ padding: 12, fontWeight: 800, color: scoreColor(s.score || 67) }}>{s.score || 67}</td>
                    <td style={{ padding: 12, fontWeight: 700, color: C.critical }}>{s.total_issues || 116} issues</td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="a11y-btn" onClick={() => handleDownloadReport("PDF")} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.primary}`, background: C.primaryBg, color: C.primary, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>PDF</button>
                        <button className="a11y-btn" onClick={() => handleDownloadReport("HTML")} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.cardBg, color: C.textMain, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>HTML</button>
                        <button className="a11y-btn" onClick={() => handleDownloadReport("JSON")} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.cardBg, color: C.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>JSON</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

